import React from 'react';
import { Box, CircularProgress, Typography, keyframes } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface AppLoaderProps {
  size?: number;
  thickness?: number;
  message?: string;
  showMessage?: boolean;
  variant?: 'default' | 'overlay' | 'inline' | 'dots';
  fullScreen?: boolean;
}

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

const AppLoader: React.FC<AppLoaderProps> = ({
  size = 40,
  thickness = 4,
  message = 'Loading...',
  showMessage = true,
  variant = 'default',
  fullScreen = true
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Theme-aware colors
  const colors = {
    background: isDarkMode ? '#000000' : '#ffffff',
    text: isDarkMode ? '#ffffff' : '#000000',
    textSecondary: isDarkMode ? '#cccccc' : '#666666',
    pulse: isDarkMode ? '#ffffff' : '#000000',
    spinner: isDarkMode ? '#000000' : '#ffffff',
    overlay: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'
  };

  const containerStyles = fullScreen
    ? {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: variant === 'overlay' ? colors.overlay : colors.background,
        zIndex: variant === 'overlay' ? 9999 : 1000,
        backdropFilter: variant === 'overlay' ? 'blur(2px)' : 'none'
      }
    : {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(2),
        minHeight: variant === 'inline' ? 'auto' : '200px'
      };

  if (variant === 'dots') {
    return (
      <Box sx={containerStyles}>
        <Box sx={{ display: 'flex', gap: 1, mb: showMessage ? 2 : 0 }}>
          {[0, 1, 2].map(index => (
            <Box
              key={index}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: colors.text,
                animation: `${bounce} 1.4s infinite ease-in-out`,
                animationDelay: `${index * 0.16}s`,
                animationFillMode: 'both'
              }}
            />
          ))}
        </Box>
        {showMessage && (
          <Typography
            variant="body2"
            sx={{
              color: colors.text,
              fontSize: '0.875rem',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: showMessage ? 3 : 0
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: size + 16,
            height: size + 16,
            borderRadius: '50%',
            backgroundColor: colors.pulse,
            animation: `${pulse} 2s infinite ease-in-out`
          }}
        />

        <CircularProgress
          size={size + 8}
          thickness={thickness * 0.5}
          sx={{
            position: 'absolute',
            color: colors.spinner,
            animationDuration: '2s'
          }}
        />

        <CircularProgress
          size={size}
          thickness={thickness}
          sx={{
            color: colors.spinner,
            filter: `drop-shadow(0 2px 8px ${isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.25)'})`,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round'
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.spinner,
            boxShadow: `0 0 10px ${isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'}`
          }}
        />
      </Box>

      {showMessage && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h6"
            sx={{
              color: colors.text,
              fontSize: '1rem',
              fontWeight: 600,
              mb: 0.5
            }}
          >
            {message}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary,
              fontSize: '0.75rem',
              opacity: 0.8,
              fontWeight: 500
            }}
          >
            Please wait a moment
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AppLoader;